import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useModal, UseModalProps } from '../../../application/hooks/useModal';
import { logger } from '../../../application/logging/logging';
import { isValidationProblemDetails, pascalToCamelCase } from '../../../helpers/error-helpers';
import { ValidationConstants, ValidationFormRules } from '../../../helpers/validation-helpers';
import { HookFormRadioGroup } from '../../../hookFormControls/hookFormRadioGroup';
import { QueryFactory } from '../../../services/api';
import Style from './issueForm.module.scss';
import { isNullOrEmpty } from '../../../helpers/string-helper';
import { FileTypes } from '../../../helpers/fileValidation-helpers';
import { AppAttachmentValueToFileParameter } from '../../../helpers/file-helper';
import {
  filterDynamicIssueTopicsFunc,
  issueSubjects,
  IssueSubjectsType,
  issueTopicsForPatient,
  IssueTopicsForPatientType,
  issueTopicsForStudy,
  IssueTopicsForStudyType,
} from '../bar/issueFilters-helper';
import {
  CreateIssueConfirmationModal,
  CreateIssueConfirmationModalParams,
} from '../createConfirmationDialog/createIssueConfirmationModal.component';
import { FileParameter } from '../../../services/api/api-client';
import { AppAttachmentsField, AppDropdownField, AppRadioGroupField } from 'uikit/fields';
import { AppAttachmentValue } from 'uikit/fields/attachments/attachmentsField/appAttachmentsField';
import { HookFormTextInput } from 'src/hookFormControls/hookFormTextInput';
import { DropdownOption } from 'uikit/inputs/dropdown/appDropdownInput';
import { useGetFormsQuery } from 'src/services/api/api-client/FormsQuery';
import { useGetSurveysQuery } from 'src/services/api/api-client/SurveyQuery';
import { LocalizedResourceDictionaryKeys } from 'src/application/localisation/i18next';
import { convertDateToString } from 'src/helpers/date-helpers';
import { Loading } from 'uikit/suspense/Loading';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { useGetPatientUniqueIdsQuery } from 'src/services/api/api-client/PatientQuery';
import { startOfTomorrow } from 'date-fns';

export type IssueFormType = {
  studyId: number;
  modal: UseModalProps<Partial<IssueCreationTypeWithId> | undefined>;
};

export type IssueCreationType = {
  subject: IssueSubjectsType;
  topic: IssueTopicsForPatientType | IssueTopicsForStudyType;
  linkedPatientUniqId?: string;
  topicAdditional?: string;
  description: string;
  deadlineAt: Date;
  recommendedAction: string;
  files: AppAttachmentValue[] | null;
  resultId?: number;
  fieldId?: string;
  stepName?: string | 'multiple' | null;
  fieldDescription?: string;
};

export type IssueCreationTypeWithId = IssueCreationType & {
  issueId?: number;
};

type IssueHookFormCreationType = Omit<IssueCreationType, 'deadlineAt'> & {
  deadlineAt: string;
};

export const IssueForm: FC<IssueFormType> = ({ studyId, modal }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const confirmationModal = useModal<CreateIssueConfirmationModalParams>();
  const study = useStudy();

  const patientsUniqueIdOptions = usePatientOptions();

  const params = useMemo(
    () => ({ ...modal.params, deadlineAt: convertDateToString(modal.params?.deadlineAt) }),
    [modal.params],
  );

  const subjects = useMemo(() => {
    return issueSubjects.map((subject, i) => ({
      key: i,
      value: subject,
      caption: t(`Issues.Subject.${subject}`),
    }));
  }, [t]);

  const {
    setError,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    unregister,
    setValue,
  } = useForm<IssueHookFormCreationType>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: params,
  });

  useLayoutEffect(() => {
    reset(params);
  }, [params, reset]);

  const subject = useWatch({ control, name: 'subject' });
  const isPatientSubject = subject === subjects[0].value;

  const topics = useMemo(() => {
    return (
      (isPatientSubject ? issueTopicsForPatient : issueTopicsForStudy)
        // @ts-ignore
        .filter((topic: IssueTopicsForStudyType | IssueTopicsForPatientType) =>
          filterDynamicIssueTopicsFunc(topic, study),
        )
        .map((topic: IssueTopicsForStudyType | IssueTopicsForPatientType, i: number) => ({
          key: i,
          value: topic,
          caption: t(`Issues.Topic.${topic}`),
        })) as { key: number; value: IssueTopicsForPatientType | IssueTopicsForStudyType; caption: string }[]
    );
  }, [isPatientSubject, t, study]);

  const topic = useWatch({ control, name: 'topic' });
  const { data: records, isInitialLoading: isRecordsLoading } = useGetFormsQuery(
    { studyId },
    { enabled: topic === 'Records', suspense: false },
  );
  const { data: surveys, isInitialLoading: isSurveysLoading } = useGetSurveysQuery(
    { studyIds: [studyId] },
    { enabled: topic === 'Surveys', suspense: false },
  );

  const topicAdditionalOptions: DropdownOption[] = useMemo(() => {
    if (topic === 'Surveys') {
      return (
        surveys?.map(
          (x) =>
            ({
              key: x.id,
              text: x.typeString,
            } as DropdownOption),
        ) || []
      );
    }

    if (topic === 'Records')
      return (
        records?.map(
          (x) =>
            ({
              key: x.id,
              text: x.type,
            } as DropdownOption),
        ) || []
      );

    return [];
  }, [records, surveys, topic]);

  useEffect(() => {
    if (!isPatientSubject) {
      unregister('linkedPatientUniqId');
      setValue('linkedPatientUniqId', undefined);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject]);

  const patchIssue = useCallback(
    async (
      data: IssueHookFormCreationType,
      uploadFileList: FileParameter[] | undefined,
      callback: () => Promise<void>,
    ) => {
      if (!params?.issueId) return;

      try {
        await QueryFactory.IssueQuery.Client.patchIssue(
          params.issueId,
          data.subject,
          data.topic,
          data.topicAdditional,
          data.description,
          data.resultId,
          data.fieldId,
          data.stepName,
          data.fieldDescription,
          new Date(Date.parse(data.deadlineAt)),
          data.linkedPatientUniqId,
          uploadFileList,
          undefined,
          data.recommendedAction,
        );

        await Promise.all([
          queryClient.invalidateQueries(QueryFactory.ProfileQuery.getDoctorProfileInfoQueryKey()),
          queryClient.invalidateQueries(QueryFactory.IssueQuery.getIssueQueryKey(params.issueId)),
          queryClient.invalidateQueries(QueryFactory.IssueQuery.getIssueHistoryQueryKey(params.issueId)),
        ]);

        await callback();
      } catch (ex: any) {
        if (ex.errors && Object.keys(ex.errors).length > 0) {
          for (const errorKey of Object.keys(ex.errors)) {
            const formKey = pascalToCamelCase(errorKey) as keyof IssueCreationType;
            setError(
              formKey,
              {
                type: 'server',
                message: ex.errors[errorKey][0],
              },
              { shouldFocus: true },
            );
          }
        } else {
          logger().error(ex);
          setError('root', {
            message: isValidationProblemDetails(ex) && !!ex.detail ? ex.detail : t('Issues.GeneralError'),
          });
        }
      }
    },
    [params.issueId, queryClient, setError, t],
  );

  const createIssue = useCallback(
    async (
      data: IssueHookFormCreationType,
      uploadFileList: FileParameter[] | undefined,
      callback: () => Promise<void>,
    ) => {
      try {
        await QueryFactory.IssueQuery.Client.addIssue(
          studyId,
          data.subject,
          data.topic,
          data.topicAdditional,
          data.description,
          data.resultId,
          data.fieldId,
          data.stepName,
          data.fieldDescription,
          new Date(Date.parse(data.deadlineAt)),
          data.linkedPatientUniqId,
          uploadFileList,
          data.recommendedAction,
        );

        await callback();
      } catch (ex: any) {
        if (ex.errors && Object.keys(ex.errors).length > 0) {
          for (const errorKey of Object.keys(ex.errors)) {
            const formKey = pascalToCamelCase(errorKey) as keyof IssueCreationType;
            setError(
              formKey,
              {
                type: 'server',
                message: ex.errors[errorKey][0],
              },
              { shouldFocus: true },
            );
          }
        } else {
          logger().error(ex);
          setError('root', {
            message: isValidationProblemDetails(ex) && !!ex.detail ? ex.detail : t('Issues.GeneralError'),
          });
        }
      }
    },
    [setError, studyId, t],
  );

  const onSubmit = handleSubmit(async (data: IssueHookFormCreationType) => {
    const isEditForm = params?.issueId;
    const uploadFileList = data.files?.map(AppAttachmentValueToFileParameter).filter((f) => f.data);
    const callback = async () => {
      await Promise.all([
        queryClient.invalidateQueries(QueryFactory.ProfileQuery.getDoctorProfileInfoQueryKey()),
        queryClient.invalidateQueries(QueryFactory.PatientQuery.getPatientsQueryKey()),
        queryClient.invalidateQueries(QueryFactory.IssueQuery.getIssuesForStudyQueryKey(studyId)),
        queryClient.invalidateQueries(QueryFactory.IssueQuery.getIssuesGroupedByPatientsQueryKey(studyId)),
      ]);
      modal.closeModal();
    };

    if (isEditForm) {
      setIsSubmitting(true);
      await patchIssue(data, uploadFileList, callback);
      setIsSubmitting(false);
    } else {
      confirmationModal.openModal({
        issue: { ...data, deadlineAt: new Date(Date.parse(data.deadlineAt)) },
        callback: async () => {
          setIsSubmitting(true);
          await createIssue(data, uploadFileList, callback);
          setIsSubmitting(false);
        },
      });
    }
  });

  const filledFields = useMemo(
    () => (
      <div className={Style.predefinedFieldContainer}>
        {params?.subject && (
          <div className={Style.row}>
            <div>{t('Issues.CreateModal.SubjectLabel' as LocalizedResourceDictionaryKeys)}</div>
            <div>
              {t(`Issues.Subject.${params?.subject}` as LocalizedResourceDictionaryKeys)}
              {params?.linkedPatientUniqId && params?.linkedPatientUniqId}
            </div>
          </div>
        )}
        {params?.topic && (
          <div className={Style.row}>
            <div>{t('Issues.CreateModal.TopicLabel' as LocalizedResourceDictionaryKeys)}</div>
            <div>{t(`Issues.Topic.${params?.topic}` as LocalizedResourceDictionaryKeys)}</div>
          </div>
        )}
        {params?.topicAdditional && (
          <div className={Style.row}>
            <div>{t('Issues.CreateModal.TopicAdditionalLabel' as LocalizedResourceDictionaryKeys)}</div>
            <div>{params?.topicAdditional}</div>
          </div>
        )}
        {params?.fieldDescription && (
          <div className={Style.row}>
            <div>{t('Issues.CreateModal.FieldDescription')}</div>
            <div>{params?.fieldDescription}</div>
          </div>
        )}
      </div>
    ),
    [params, t],
  );

  const content = useMemo(() => {
    return (
      <form id={'issueCreationForm'} onSubmit={onSubmit} autoComplete={'off'} noValidate={true}>
        {!params?.issueId && <>{filledFields}</>}
        {(!params?.subject || params.issueId) && (
          <HookFormRadioGroup
            labelProps={{ text: t('Issues.CreateModal.SubjectLabel'), isBold: true }}
            options={subjects}
            name={'subject'}
            labelField={'caption'}
            valueField={'value'}
            control={control}
            rules={{
              required: {
                value: true,
                message: t('LocalErrors.FieldRequired'),
              },
            }}
            disabled={isSubmitting || !!params?.subject}
            errorProps={{ errors: errors?.subject?.message }}
          />
        )}

        {(!params?.linkedPatientUniqId || params.issueId) && isPatientSubject && (
          <Controller
            control={control}
            name={'linkedPatientUniqId'}
            rules={{ ...ValidationFormRules().requiredRule }}
            render={({ field: { onChange, value, ref } }) => (
              <div tabIndex={0} ref={ref}>
                <AppDropdownField
                  isMultiple={false}
                  isRequired
                  suggestion
                  labelProps={{
                    text: t('Issues.CreateModal.PatientIdLabel'),
                    isBold: true,
                  }}
                  placeholder={t('Issues.CreateModal.PatientIdPlaceholder')}
                  disabled={isSubmitting || !!params?.linkedPatientUniqId}
                  errorProps={{
                    errors: errors?.linkedPatientUniqId?.message,
                  }}
                  options={patientsUniqueIdOptions}
                  value={patientsUniqueIdOptions.find((x) => x.key === value)}
                  onChange={(option) => onChange(option?.key)}
                />
              </div>
            )}
          />
        )}

        {!isNullOrEmpty(subject) && (
          <>
            {(!params?.topic || params.issueId) && (
              <Controller
                control={control}
                name={'topic'}
                rules={{ ...ValidationFormRules().requiredRule }}
                render={({ field: { onChange, value, ref } }) => (
                  <div tabIndex={0} ref={ref}>
                    <AppRadioGroupField
                      valueField={'value'}
                      labelField={'caption'}
                      labelProps={{ text: t('Issues.CreateModal.TopicLabel'), isBold: true }}
                      isVertical={true}
                      options={topics}
                      disabled={isSubmitting || !!params?.topic}
                      errorProps={{ errors: errors?.topic?.message }}
                      value={value}
                      onChange={(x) => {
                        onChange(x.value);
                        setValue('topicAdditional', undefined);
                      }}
                    />
                  </div>
                )}
              />
            )}

            {(topic === 'Records' || topic === 'Surveys') && (!params?.topicAdditional || params.issueId) && (
              <Loading
                loading={isSurveysLoading || isRecordsLoading}
                loaderClassName={Style.fieldLoader}
                testId={'issue-form'}
              >
                {!!topicAdditionalOptions.length && (
                  <Controller
                    control={control}
                    name={'topicAdditional'}
                    rules={{ ...ValidationFormRules().requiredRule }}
                    render={({ field: { onChange, value } }) => (
                      <AppDropdownField<DropdownOption>
                        isMultiple={true}
                        labelProps={{ text: t('Issues.CreateModal.TopicAdditionalLabel'), isBold: true }}
                        placeholder={t('Issues.CreateModal.TopicAdditionalDropdownPlaceholder')}
                        isRequired={true}
                        options={topicAdditionalOptions}
                        disabled={isSubmitting || !!params?.topicAdditional}
                        errorProps={{ errors: errors?.topicAdditional?.message }}
                        value={topicAdditionalOptions.filter((x) => value?.includes(x.text))}
                        onChange={(x) => {
                          onChange(x?.map((o) => o.text).join(', '));
                        }}
                      />
                    )}
                  />
                )}
              </Loading>
            )}

            {topic === 'Other' && (
              <HookFormTextInput
                labelProps={{ text: t('Issues.CreateModal.TopicAdditionalLabel'), isBold: true }}
                placeholder={t('Issues.CreateModal.TopicAdditionalTextInputPlaceholder')}
                control={control}
                name={'topicAdditional'}
                rules={{
                  ...ValidationFormRules().maxLengthRule,
                  ...ValidationFormRules().requiredRule,
                }}
                disabled={isSubmitting || !!params?.topicAdditional}
              />
            )}

            <HookFormTextInput
              labelProps={{ text: t('Issues.CreateModal.DescriptionLabel'), isBold: true }}
              placeholder={t('Issues.CreateModal.DescriptionPlaceholder')}
              control={control}
              name={'description'}
              rules={{
                ...ValidationFormRules().maxLengthRule,
                ...ValidationFormRules().requiredRule,
              }}
              type={'text-area'}
              maxLength={ValidationConstants.textInputMaxLength}
              disabled={isSubmitting || !!params?.description}
            />

            <HookFormTextInput
              labelProps={{ text: t('Issues.CreateModal.ActionLabel'), isBold: true }}
              placeholder={t('Issues.CreateModal.ActionPlaceholder')}
              control={control}
              name={'recommendedAction'}
              rules={{
                ...ValidationFormRules().requiredRule,
                maxLength: {
                  value: 250,
                  message: t('LocalErrors.LengthIsExceeded'),
                },
              }}
              type={'text-area'}
              maxLength={250}
              disabled={isSubmitting || !!params?.recommendedAction}
            />

            <HookFormTextInput
              type={'date'}
              control={control}
              name={'deadlineAt'}
              min={convertDateToString(startOfTomorrow())}
              labelProps={{ text: t('Issues.CreateModal.DeadlineLabel'), isBold: true }}
              rules={{ ...ValidationFormRules().requiredRule, ...ValidationFormRules().issueDateRule }}
              disabled={isSubmitting}
            />

            <Controller
              control={control}
              name={'files'}
              rules={{
                validate: ValidationFormRules().filesValidate,
              }}
              render={({ field: { onChange, value } }) => (
                <AppAttachmentsField
                  label={t('Forms.Controls.Attachments.FieldLabel')}
                  isBold={true}
                  onChange={onChange}
                  value={value}
                  disabled={isSubmitting || !!params?.files}
                  fileTypes={FileTypes}
                  multiple={true}
                  error={!!(errors.files as any)?.message}
                  errorText={(errors.files as any)?.message}
                />
              )}
            />
          </>
        )}
      </form>
    );
  }, [
    onSubmit,
    params.issueId,
    params?.subject,
    params?.linkedPatientUniqId,
    params?.topic,
    params?.topicAdditional,
    params?.description,
    params?.recommendedAction,
    params?.files,
    filledFields,
    t,
    subjects,
    control,
    isSubmitting,
    errors?.subject?.message,
    errors?.linkedPatientUniqId?.message,
    errors?.topic?.message,
    errors?.topicAdditional?.message,
    errors.files,
    isPatientSubject,
    subject,
    topic,
    isSurveysLoading,
    isRecordsLoading,
    topicAdditionalOptions,
    patientsUniqueIdOptions,
    topics,
    setValue,
  ]);

  return (
    <>
      <DialogModal
        containerClassName={Style.modal}
        bodyClassName={Style.modalBody}
        title={
          params?.issueId
            ? t('Issues.CreateModal.EditTitle', { issueId: params.issueId })
            : t('Issues.CreateModal.CreateTitle')
        }
        visible={modal.visible}
        onHide={modal.closeModal}
        footer={{
          errors: errors?.root?.message,
          leftButton: {
            text: t('Common_Cancel'),
            onClick: modal.closeModal,
            disabled: isSubmitting,
          },
          rightButton: {
            text: t('Common_Finish'),
            type: 'submit',
            disabled: isSubmitting,
            isLoading: isSubmitting,
            form: 'issueCreationForm',
          },
        }}
        onDismissed={reset}
        isDisabled={isSubmitting}
        testId={'add-issue-modal'}
      >
        {content}
      </DialogModal>
      <CreateIssueConfirmationModal modal={confirmationModal} />
    </>
  );
};

const usePatientOptions = () => {
  const study = useStudy();
  const { data: uniqIds } = useGetPatientUniqueIdsQuery(study?.id, { enabled: !!study?.id });

  return (
    uniqIds?.map(
      (x) =>
        ({
          key: x,
          text: x,
        } satisfies DropdownOption),
    ) ?? []
  );
};
